import { VgApiResult, vgApiRequest } from "./vg-api.service";

/**
 * Settling an invoice.
 *
 * Until this existed an invoice could be raised and never marked paid, so every
 * bill a clinic ever issued read as outstanding - which is worse than showing no
 * status at all, because it looks like information.
 *
 * A payment is its own row against the invoice, not a flag on it. That means part
 * payments work without any extra machinery: two payments of 25 against a bill of
 * 50 is two rows, and the remaining balance falls out of the arithmetic rather
 * than being stored somewhere that can disagree with it.
 */

/** Mirrors ENUM_SYS_Payment_Type. 1-based, like every Core enum here. */
export const PAYMENT_TYPE = {
  UNKNOWN: 1,
  CASH: 2,
  CARD: 3,
  PAYPAL: 4,
} as const;

export const paymentTypeOptions = [
  { title: "Cash", value: String(PAYMENT_TYPE.CASH) },
  { title: "Card", value: String(PAYMENT_TYPE.CARD) },
  { title: "PayPal", value: String(PAYMENT_TYPE.PAYPAL) },
];

export const getPaymentTypeLabel = (value: number | undefined) =>
  ({
    [PAYMENT_TYPE.CASH]: "Cash",
    [PAYMENT_TYPE.CARD]: "Card",
    [PAYMENT_TYPE.PAYPAL]: "PayPal",
  }[value ?? PAYMENT_TYPE.UNKNOWN] ?? "Unknown");

export type PaymentInput = {
  orgId: number;
  invoiceId: number;
  contactId: number;
  amount: number;
  currency: number;
  paymentType: number;
  /** ISO. Defaults to now, because a payment recorded with no date is unreconcilable. */
  paymentDate?: string;
};

export async function recordPayment(
  accessToken: string,
  input: PaymentInput
): Promise<VgApiResult<{ newRecordID?: number; NewRecordID?: number }>> {
  if (!Number.isFinite(input.amount) || input.amount <= 0) {
    return { data: null, error: { message: "A payment must be more than zero." } };
  }
  if (input.paymentType === PAYMENT_TYPE.UNKNOWN) {
    return { data: null, error: { message: "Choose how this was paid." } };
  }

  return vgApiRequest(accessToken, "Create_Org_Payment", {
    orgId: input.orgId,
    invoiceId: input.invoiceId,
    contactId: input.contactId,
    monetaryAmount: input.amount,
    monetaryCurrency: input.currency,
    paymentType: input.paymentType,
    paymentDate: input.paymentDate ?? new Date().toISOString(),
  });
}

/**
 * Removes a payment - a mistyped amount, or one recorded against the wrong
 * invoice. The invoice's balance is recomputed from what remains.
 */
export async function deletePayment(
  accessToken: string,
  orgId: number,
  paymentId: number
): Promise<VgApiResult<unknown>> {
  return vgApiRequest(accessToken, "Delete_Org_Payment_By_Payment_ID", {
    orgId,
    paymentId,
  });
}

export type Payment = {
  paymentId: number;
  amount?: number;
  currency?: number;
  paymentType?: number;
  paymentDate?: string;
};

const num = (v: unknown) => (v != null ? Number(v) : undefined);
const pick = (o: Record<string, unknown>, camel: string) =>
  o[camel] ?? o[camel.charAt(0).toUpperCase() + camel.slice(1)];

function money(raw: unknown): number | undefined {
  if (raw == null) return undefined;
  if (typeof raw === "number") return raw;
  const o = raw as Record<string, unknown>;
  return num(pick(o, "monetaryAmount") ?? pick(o, "amount"));
}

export function mapPayment(raw: unknown): Payment {
  const o = (raw ?? {}) as Record<string, unknown>;
  return {
    paymentId: Number(pick(o, "paymentId") ?? 0),
    amount: money(pick(o, "computedMoneyValue") ?? pick(o, "monetaryAmount")),
    currency: num(pick(o, "monetaryCurrency")),
    paymentType: num(pick(o, "paymentType")),
    paymentDate: pick(o, "paymentDate")
      ? String(pick(o, "paymentDate"))
      : undefined,
  };
}

/**
 * What is still owed, computed rather than trusted.
 *
 * The invoice carries a remainingBalance, but it is only as fresh as the last
 * read; a payment recorded a moment ago may not be in it. Subtracting what is on
 * the invoice from its total is arithmetic anyone can check, and cannot disagree
 * with the payments listed beside it.
 */
export function outstanding(
  total: number | undefined,
  payments: Payment[]
): number | undefined {
  if (total == null) return undefined;
  const paid = payments.reduce((sum, p) => sum + (p.amount ?? 0), 0);
  return Math.round((total - paid) * 100) / 100;
}
